import styled from 'styled-components';
import { FontFamily } from 'styled/fonts/font-family';
import { Grid } from 'styled/layouts/grid';
import {
  TypeSubhead,
  TypeCaption,
  TypeDisclaimer,
} from 'components/library/Typography';
import { mqLargeMin, mqLargeMax } from 'styled/tools/media-queries';
import { motion } from 'motion/react';

export const FooterImageWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 80px;

  @media ${mqLargeMin} {
    margin-top: -60px !important;
    height: 192px;
  }
`;

export const FooterWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 316px;
`;
// so we don't see fixed content before nav on scroll (clip it)
export const FooterClip = styled.div`
  clip-path: inset(1px 0 0 0);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const FooterInner = styled(Grid)`
  background-color: var(--fill-brand);
  color: var(--fill-primary);
  padding-top: var(--spacing-32);
  padding-left: var(--spacing-16);
  padding-right: var(--spacing-16);
  padding-bottom: var(--spacing-32);
  position: relative;
  z-index: 3;
  row-gap: var(--spacing-32);

  @media ${mqLargeMin} {
    padding: var(--spacing-64);
  }
`;

export const FooterContent = styled.div`
  & > * + * {
    margin-top: var(--spacing-32);
  }
  grid-column: 1 / -1;
  display: grid;
  flex-direction: column;
  justify-content: space-between;

  > a {
    text-align: center;

    @media ${mqLargeMin} {
      text-align: left;
    }

    @media ${mqLargeMax} {
      margin-block: var(--spacing-40);
    }
  }

  @media ${mqLargeMin} {
    grid-column: auto / span 6;
    margin-bottom: 83px;
  }
`;

export const FooterHeading = styled.div`
  & > * + * {
    margin-top: var(--spacing-32);
  }
`;

export const FooterSubhead = styled(TypeSubhead)`
  color: var(--fill-primary);
  font-feature-settings: 'ss05' on;
  font-family: var(--ff, 'Seed Sans'); !important;
  letter-spacing: 0 !important; // Necessary for font glyphs to work

  @media ${mqLargeMax} {
    font-size: var(--font-size-600);
    font-weight: 350;
    line-height: 120%;
  }

  svg {
    margin-bottom: -6px;
  }

  svg:nth-of-type(3) {
    margin-bottom: 0px;
    padding: 0px 6px;
    width: 48px;
  }
`;

export const FooterCaption = styled(TypeCaption)`
  color: var(--fill-primary);
  opacity: 0.5;
  font-size: var(--font-size-350);
`;

export const FooterForm = styled.form`
  display: flex;
  flex-direction: column;

  & label {
    color: var(--fill-primary);
  }
`;

export const Input = styled.input`
  &[type='email'] {
    background: transparent;
    appearance: none;
    color: var(--fill-primary);
    border: 2px solid var(--fill-primary);
    border-radius: var(--radius-sm);
    padding: 14px 20px;
    width: 100%;
    height: 52px;
  }

  &:focus-visible {
    outline: 1px solid var(--guidance-primary);
  }

  &::placeholder {
    opacity: 0.5;
    color: var(--fill-primary);
    font-size: var(--font-size-350);

    @media (${mqLargeMin}) {
      opacity: 1;
    }
  }

  &:focus::placeholder {
    opacity: 0;
  }
`;

export const FooterNewsletter = styled.div`
  position: relative;
  max-width: 425px;
  width: 100%;
  margin-top: var(--spacing-16);
`;

export const Submit = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: var(--fill-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline: var(--spacing-16);
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate3d(0, -50%, 0);
  height: 100%;
  z-index: 2;
  width: 43px;
`;

export const FooterFixedBottom = styled.div`
  position: fixed;
  bottom: 0;
  background-size: cover;
  background-position: center;
  width: 100%;
  min-height: 800px;
  z-index: -1;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    background-blend-mode: multiply;
    background: rgba(59, 82, 40, 0.48);
  }
`;

export const FixedHeadlineWrapper = styled.div`
  padding-block: 350px 142px;
  margin-top: 242px;
  position: relative;
  /* filter: url(#threshold) blur(0.6px); */

  & svg {
    width: 0;
    height: 0;
  }
`;

export const FixedHeadline = styled(motion.div)`
  color: var(--fill-primary);
  font-family: var(--ff, ${FontFamily.sansMedium});
  font-size: var(--font-size-1200);
  font-weight: 500;
  position: absolute;
  width: 100%;
  display: inline-block;
  user-select: none;
  text-align: center;
  will-change: filter, opacity;

  @media ${mqLargeMin} {
    font-size: var(--font-size-1600);
  }
`;

export const Copyright = styled(TypeDisclaimer)`
  text-align: center;
  color: var(--fill-primary);
`;

export const FormAlert = styled.div<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || 'var(--fill-brand)'};
  color: var(--fill-primary);
  border-radius: var(--bdrs-s);
  font-size: var(--font-size-350);
  font-weight: 350;
  line-height: 140%;
  padding: 1em 1.45em;
  margin-top: var(--spacing-16);
  max-width: 425px;
`;

/* Stacks the footer nav links with the country selector so the selector sits
   directly below the links grid in the same column, per design. */
export const FooterNavColumn = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-32);

  @media ${mqLargeMin} {
    grid-column: 8 / span 5;
  }
`;

export const FooterCountrySelectorArea = styled.div`
  /* The Menu panel centers on the mobile viewport; tell it how far the
     trigger is inset (FooterInner's 16px horizontal padding). */
  --menu-viewport-inset: var(--spacing-16);
  /* Snow-white menu button (icons + chevron) on the dark footer. */
  --menu-color: var(--text-inverse-primary);

  /* CountrySelector renders nothing while markets load (or with <2 markets);
     collapse so the column gap doesn't leave a blank band. */
  &:empty {
    display: none;
  }
`;

export const FooterMobileDisclaimer = styled.div`
  display: block;
  padding: var(--spacing-8);
  align-self: stretch;
  border-radius: var(--radius-md);
  border: var(--stroke-default) solid var(--outline-light-20);

  flex: 1 0 0;
  font-size: var(--font-size-300);
  font-weight: var(--font-weight-500);
  line-height: 1.4;
  color: var(--text-inverse-primary);

  sup {
    /* Relative to parent disclaimer text — scales if body copy size changes */
    font-size: 0.8em;
    line-height: 1;
    vertical-align: middle;
    color: var(--text-inverse-primary);
    top: 0;
  }
`;

export const FooterDesktopDisclaimer = styled.div`
  /* block (not flex): innerHTML mixes <sup>, text, <br>; flex items + line-height:0 on sup hid † and * */
  display: block;
  padding: var(--spacing-8);
  border-radius: var(--radius-sm);
  border: var(--stroke-default) solid var(--outline-light-20);
  width: 400px;

  flex: 1 0 0;
  font-size: var(--font-size-300);
  font-weight: var(--font-weight-500);
  line-height: 1.4;
  color: var(--text-inverse-primary);

  sup {
    /* Relative to parent disclaimer text — scales if body copy size changes */
    font-size: 0.8em;
    line-height: 1;
    vertical-align: middle;
    color: var(--text-inverse-primary);
    top: 0;
  }
`;

/* Disclaimer box that leads the footer above the Seed logo (cobiotics PDPs).
   Fills its column; mobile pads to 12px (no --spacing-12, so the size primitive). */
export const FooterTopDisclaimer = styled(FooterDesktopDisclaimer)`
  width: 100%;
  border-radius: var(--radius-xs);

  @media ${mqLargeMax} {
    padding: var(--size-300);
    border-radius: var(--radius-sm);
  }
`;

export const SecondaryDisclaimerWrapper = styled.div`
  color: var(--fill-primary);
  font-size: var(--font-size-250);
  font-weight: 350;
  line-height: 1.4;
  letter-spacing: -0.05px;
  padding-left: 0;
  margin-top: var(--spacing-16);

  & + ${FooterDesktopDisclaimer}, & + ${FooterMobileDisclaimer} {
    margin-top: var(--spacing-8);
  }

  p {
    margin: 0;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    font-weight: inherit;
    color: inherit;
  }

  p sup {
    /* Relative to inherited disclaimer font-size */
    font-size: 0.8em;
    color: inherit;
    line-height: 1;
    vertical-align: middle;
  }
`;
